/** Table primitives (MASTER §6): sticky uppercase header, hover rows, mono numerics. */
import type { HTMLAttributes, ReactNode, ThHTMLAttributes, TdHTMLAttributes } from 'react';

import { cn } from './cn.js';
import { Skeleton } from './Feedback.js';

export function TableWrap({ className, ...rest }: HTMLAttributes<HTMLDivElement>): ReactNode {
  return <div className={cn('w-full overflow-x-auto', className)} {...rest} />;
}

export function Table({ className, ...rest }: HTMLAttributes<HTMLTableElement>): ReactNode {
  return <table className={cn('w-full border-collapse text-sm', className)} {...rest} />;
}

export function THead({ className, ...rest }: HTMLAttributes<HTMLTableSectionElement>): ReactNode {
  return (
    <thead
      className={cn(
        'sticky top-0 z-10 bg-surface-2 text-left text-xs font-semibold uppercase tracking-wide text-muted',
        className,
      )}
      {...rest}
    />
  );
}

export function TH({
  className,
  numeric,
  ...rest
}: ThHTMLAttributes<HTMLTableCellElement> & { numeric?: boolean }): ReactNode {
  return (
    <th
      scope="col"
      className={cn('h-10 px-3 font-semibold', numeric && 'text-right', className)}
      {...rest}
    />
  );
}

export function TR({
  className,
  clickable,
  ...rest
}: HTMLAttributes<HTMLTableRowElement> & { clickable?: boolean }): ReactNode {
  return (
    <tr
      className={cn(
        'border-t border-border transition-colors',
        clickable && 'cursor-pointer hover:bg-surface-2',
        className,
      )}
      {...rest}
    />
  );
}

export function TD({
  className,
  numeric,
  ...rest
}: TdHTMLAttributes<HTMLTableCellElement> & { numeric?: boolean }): ReactNode {
  return (
    <td
      className={cn('h-11 px-3 align-middle text-fg', numeric && 'text-right tnum', className)}
      {...rest}
    />
  );
}

/** Placeholder rows while a table loads (no layout jump). */
export function TableSkeleton({ rows = 6, cols = 4 }: { rows?: number; cols?: number }): ReactNode {
  return (
    <tbody>
      {Array.from({ length: rows }).map((_, r) => (
        <tr key={r} className="border-t border-border">
          {Array.from({ length: cols }).map((__, c) => (
            <td key={c} className="h-11 px-3">
              <Skeleton className={cn('h-3.5', c === 0 ? 'w-32' : 'w-16')} />
            </td>
          ))}
        </tr>
      ))}
    </tbody>
  );
}
